import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
  MouseEvent,
  createRef,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { t } from '@lingui/macro';

import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';

import Button, {
  type ButtonProps,
} from '@decisiv/ui-components/lib/components/Button';
import { type ModalProps } from '@decisiv/ui-components/lib/components/Modal/types';
import { type FormHandler } from 'components/Form';
import { type SavedContextState, type StepOneFormValues } from '../types';

// The Wizard renders its ghost "Cancel" in the interaction blue; the design
// calls for the muted Alaskan Husky grey instead.
const CANCEL_COLOR = toColorString(color.base.alaskanHusky);

export const stepOneFormRef = createRef<FormHandler<StepOneFormValues>>();

interface WizardModalHandles {
  accountId?: string;
  depotId?: string;
  stepOneProps?: {
    nextButtonProps: {
      onClick: () => void;
      disabled: boolean;
    };
  };
  stepTwoProps?: {
    previousButtonProps: {
      onClick: () => void;
    };
    nextButtonProps: {
      onClick: () => void;
      disabled: boolean;
    };
  };
  stepThreeProps?: {
    previousButtonProps: {
      onClick: () => void;
    };
    nextButtonProps: {
      onClick: () => void;
      disabled: boolean;
    };
  };
  stepFourProps?: Record<string, never>;
  wizardProps: {
    activeStep: number;
    onCancel: NonNullable<ModalProps['onClose']>;
    submitButtonProps: ButtonProps;
  };
  savedState: SavedContextState;
  setSavedState: Dispatch<SetStateAction<SavedContextState>>;
  forward: () => void;
  back: () => void;
  /**
   * When true, the wizard shows its intermediate ("between steps") state: the
   * active step's `intermediateContent` replaces the body, the step tracker
   * shows the half-filled connector, and the nav actions are disabled. Used
   * while VINs are validated (Select → Review) and assets are added
   * (Add Unit # → Summary).
   */
  intermediate: boolean;
  setIntermediate: Dispatch<SetStateAction<boolean>>;
  setStepOneContinueButtonEnabled: Dispatch<SetStateAction<boolean>>;
  setStepOneContinueButtonLoading: Dispatch<SetStateAction<boolean>>;
  setStepTwoContinueButtonEnabled: Dispatch<SetStateAction<boolean>>;
  setStepThreeContinueButtonEnabled: Dispatch<SetStateAction<boolean>>;
}

/* istanbul ignore next -- @preserve */
export const WizardContext = createContext<WizardModalHandles>(
  {} as WizardModalHandles,
);

interface AddAssetProviderProps {
  children: ReactNode;
  onClose?: NonNullable<ModalProps['onClose']>;
  depotId?: string;
  accountId?: string;
}

const AddAssetProvider = ({
  children,
  onClose,
  accountId,
  depotId,
}: AddAssetProviderProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [intermediate, setIntermediate] = useState(false);
  const [savedState, setSavedState] = useState<SavedContextState>({
    stepOne: {
      accountId,
      depotId,
      vins: [],
    },
    stepTwo: {
      batchId: undefined,
      validVins: [],
      invalidVins: [],
      usedByOther: [],
      usedByOtherDepot: [],
      ownedByCurrent: [],
    },
    addUnit: {
      unitNumbers: {},
    },
    registration: {
      assetsRegistrationId: undefined,
      reportUrl: undefined,
      assetsAdded: [],
      assetsFailed: [],
    },
  });

  const [stepOneContinueButtonEnabled, setStepOneContinueButtonEnabled] =
    useState(false);
  const [stepOneContinueButtonLoading, setStepOneContinueButtonLoading] =
    useState(false);
  const [stepTwoContinueButtonEnabled, setStepTwoContinueButtonEnabled] =
    useState(false);
  const [stepThreeContinueButtonEnabled, setStepThreeContinueButtonEnabled] =
    useState(false);

  const hideModal = useCallback(
    (event: MouseEvent<unknown> | KeyboardEvent) => {
      setCurrentStep(1);
      setIntermediate(false);
      onClose?.(event);
    },
    [onClose],
  );

  const back = useCallback(() => setCurrentStep((prev) => prev - 1), []);
  const forward = useCallback(() => setCurrentStep((prev) => prev + 1), []);

  const stepOneProps = useMemo(
    () => ({
      nextButtonProps: {
        onClick: () => stepOneFormRef.current?.submit?.(),
        disabled: !stepOneContinueButtonEnabled,
        loading: stepOneContinueButtonLoading,
        loadingText: t`Loading...`,
      },
    }),
    [stepOneContinueButtonEnabled, stepOneContinueButtonLoading],
  );

  const stepTwoProps = useMemo(
    () => ({
      previousButtonProps: {
        onClick: back,
      },
      nextButtonProps: {
        onClick: forward,
        disabled: !stepTwoContinueButtonEnabled,
      },
    }),
    [back, forward, stepTwoContinueButtonEnabled],
  );

  const stepThreeProps = useMemo(
    () => ({
      previousButtonProps: {
        onClick: back,
      },
      nextButtonProps: {
        // Enter the intermediate state: AddingAssets registers the assets and
        // advances to Summary when registration completes.
        onClick: () => setIntermediate(true),
        disabled: !stepThreeContinueButtonEnabled,
      },
    }),
    [back, stepThreeContinueButtonEnabled],
  );

  const stepFourProps = useMemo(() => ({}), []);

  const wizardProps = useMemo(
    () => ({
      activeStep: currentStep,
      intermediate,
      onCancel: hideModal,
      submitButtonProps: {
        text: t`Close`,
        onClick: hideModal,
      },
      cancelButtonProps: {
        style: { color: CANCEL_COLOR },
      },
      renderActions: (ctx: {
        isLastStep: boolean;
        stepIndex: number;
        defaultActions: ReactNode;
      }) => {
        // The last step (Summary) is terminal: only "Close" belongs there, not
        // the ghost "Cancel" the Wizard renders by default on every step. The
        // close-toast (PD-3735) is deferred, so Close here just dismisses.
        if (ctx.isLastStep) {
          return <Button text={t`Close`} onClick={hideModal} />;
        }

        // Adding Assets intermediate (Add Unit # → Summary): show only the
        // loading indicator while registration runs — no Close/Cancel/Back
        // here. Close appears once the wizard settles on the Summary step.
        if (intermediate && ctx.stepIndex === 2) {
          return (
            <Button
              text={t`Adding Assets…`}
              loading
              loadingText={t`Adding Assets…`}
            />
          );
        }

        return ctx.defaultActions;
      },
    }),
    [currentStep, intermediate, hideModal],
  );

  const value = {
    accountId,
    depotId,
    stepOneProps,
    stepThreeProps,
    stepFourProps,
    stepTwoProps,
    wizardProps,
    savedState,
    setSavedState,
    forward,
    back,
    intermediate,
    setIntermediate,
    setStepOneContinueButtonEnabled,
    setStepOneContinueButtonLoading,
    setStepTwoContinueButtonEnabled,
    setStepThreeContinueButtonEnabled,
  } satisfies WizardModalHandles;

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
};

export const useAddAssetContext = () => useContext(WizardContext);

export default AddAssetProvider;
